/**
 * Liturgical color utilities
 */

import type { LiturgicalColor, LiturgicalSeason } from './types';

/** Human-readable color name */
export function getColorName(color: LiturgicalColor): string {
  const names: Record<LiturgicalColor, string> = {
    green: 'Green',
    white: 'White',
    red: 'Red',
    purple: 'Purple (Violet)',
    rose: 'Rose',
    black: 'Black',
    gold: 'Gold',
  };
  return names[color];
}

/** Tailwind-compatible CSS class for the liturgical color */
export function getColorClass(color: LiturgicalColor): string {
  return `bg-vestment-${color}`;
}

/** CSS color value for the liturgical color (for dynamic styles) */
export function getColorHex(color: LiturgicalColor): string {
  const colors: Record<LiturgicalColor, string> = {
    red: '#9b2226',
    white: '#f5f0e8',
    green: '#2d6a4f',
    purple: '#4a2463',
    rose: '#c44d7d',
    black: '#1f1f1f',
    gold: '#b8860b',
  };
  return colors[color];
}

/**
 * A dark, readable color to use as a nav/button background for any vestment.
 * White vestments would be invisible on a white button, so we map them to
 * a visible equivalent.
 */
export function getNavColorHex(color: LiturgicalColor): string {
  const colors: Record<LiturgicalColor, string> = {
    red: '#9b2226',
    white: '#78716c', // stone-500 — readable stand-in for white vestments
    green: '#2d6a4f',
    purple: '#4a2463',
    rose: '#c44d7d',
    black: '#44403c', // stone-700 — slightly lighter than pure black
    gold: '#92400e', // amber-800 — readable stand-in
  };
  return colors[color];
}

/** A text-safe color (for use on white/light backgrounds) */
export function getColorTextClass(color: LiturgicalColor): string {
  const classes: Record<LiturgicalColor, string> = {
    red: 'text-vestment-red',
    white: 'text-vestment-gold',
    green: 'text-vestment-green',
    purple: 'text-vestment-purple',
    rose: 'text-vestment-rose',
    black: 'text-vestment-black',
    gold: 'text-vestment-gold',
  };
  return classes[color];
}

/** Human-readable season name */
export function getSeasonName(season: LiturgicalSeason): string {
  const names: Record<LiturgicalSeason, string> = {
    'advent': 'Advent',
    'christmas': 'Christmas Season',
    'ordinary-time': 'Ordinary Time',
    'lent': 'Lent',
    'easter-triduum': 'Easter Triduum',
    'easter': 'Easter Season',
  };
  return names[season];
}

/** Human-readable rank name */
export function getRankName(rank: string): string {
  const names: Record<string, string> = {
    'solemnity': 'Solemnity',
    'feast': 'Feast',
    'memorial': 'Memorial',
    'optional-memorial': 'Optional Memorial',
    'weekday': 'Weekday',
  };
  return names[rank] ?? rank;
}
